import { Box, Button } from '@material-ui/core';
import React, { Fragment, useEffect, useMemo } from 'react';
import { HashLink } from 'react-router-hash-link';

import { CustomMenuMobile } from '@/components/shared/components/CustomMenuMobile';
import { Font } from '@/components/shared/components/Font';
import { Text } from '@/components/shared/components/Text';
import { Widgets } from '@/components/views/DynamicPageView/components/Widgets';
import { usePageRightMenu } from '@/components/views/DynamicPageView/hooks/usePageRightMenu';
import { usePageContext } from '@/contexts/PageContext/PageContext';
import { useCustomTheme } from '@/hooks/useCustomTheme';
import { useScreen } from '@/hooks/useScreen';
import { useUIWrapper } from '@/hooks/useUIWrapper';

export function PageRightMenu({ open, currentSectionIndex }: { open: boolean; currentSectionIndex: number }) {
  const {
    state: { showMobilePageRightMenu, constructing, loading },
    actions: { setShowMobilePageRightMenu },
  } = usePageContext();
  const { md, sm } = useScreen();
  const { palette, background } = useCustomTheme();
  const { pageRightMenuWidth } = useUIWrapper();

  const yPosition = open ? 0 : -10;
  const sectionIndex = currentSectionIndex || 0;
  const openedOpacity = loading ? 0.4 : 1;

  const wrapperStyle = {
    position: 'fixed',
    top: '36px',
    left: `calc(100vw - ${open ? pageRightMenuWidth : 0}px - 20px)`,
    height: 'calc(100dvh - 36px)',
    zIndex: 20,
    backgroundColor: background,
    opacity: open ? openedOpacity : 0,
    transform: `translateY(${yPosition}px)`,
    transition: 'width 0.6s ease',
    pointerEvents: loading || !open ? 'none' : 'inherit',
    overflowY: 'auto',
    overflowX: 'hidden',
  } as React.CSSProperties;

  const wrapperStyleMobile = {
    position: 'fixed',
    height: '100dvh',
    top: 0,
    left: 0,
    boxSizing: 'border-box',
    paddingTop: '24px',
    paddingBottom: '60px',
    paddingLeft: '14px',
    backgroundColor: background,
    borderLeft: `solid 2px ${palette.grey[200]}`,
    opacity: openedOpacity,
    transition: 'width 0.6s ease',
    pointerEvents: loading || !open || !showMobilePageRightMenu ? 'none' : 'inherit',
    overflowY: 'auto',
    overflowX: 'hidden',
  } as React.CSSProperties;

  useEffect(() => {
    if (md) {
      setShowMobilePageRightMenu(false);
    }
  }, [md]);

  const pageRightMenuComponent = useMemo(() => <PageRightMenuComponent sectionIndex={sectionIndex} />, [sectionIndex]);

  const desktopDisplay = open && md ? { display: 'block' } : { display: 'none' };
  const desktopMobile = open && !md ? { display: 'block' } : { display: 'none' };

  return (
    <>
      {/* Desktop version */}
      <Box width={open ? pageRightMenuWidth + 24 : 0} paddingLeft={open ? '24px' : 0} style={desktopDisplay}>
        {!constructing && (
          <Box width={pageRightMenuWidth} style={wrapperStyle}>
            {pageRightMenuComponent}
          </Box>
        )}
      </Box>

      {/* Mobile version */}
      <div style={{ opacity: openedOpacity, ...desktopMobile }}>
        <CustomMenuMobile
          isOpen={showMobilePageRightMenu}
          setIsOpen={setShowMobilePageRightMenu}
          render={
            <Box width={sm ? pageRightMenuWidth : '100%'} style={wrapperStyleMobile}>
              {!constructing && pageRightMenuComponent}
            </Box>
          }
          indentLeft={60}
          iconProps={{ icon: 'more_vert', bgColor: palette.grey[200], color: palette.primary.contrastText }}
          menuStyle={{ zIndex: 798, maxWidth: sm ? `${pageRightMenuWidth}px` : '100%' }}
          buttonStyle={{ zIndex: sm ? 900 : 799 }}
        />
      </div>
    </>
  );
}

export function PageRightMenuComponent({ sectionIndex }: { sectionIndex: number }) {
  const {
    state: { loading },
  } = usePageContext();
  const { sectionsPageRightMenu } = usePageRightMenu();
  const { palette } = useCustomTheme();

  const indicatorLineStyle = {
    position: 'absolute',
    marginTop: 35 * sectionIndex - 2,
    height: '29px',
    marginLeft: '-16px',
    width: '2px',
    transition: 'all 0.4s',
    background: palette.text.title,
  } as React.CSSProperties;

  const linkTextCommonStyle = {
    color: palette.text.secondary,
  };
  const linkTextStyle = {
    default: {
      ...linkTextCommonStyle,
      letterSpacing: '0.35px',
      fontWeight: '400',
    },
    active: {
      ...linkTextCommonStyle,
      letterSpacing: '0px',
      fontWeight: 'bold',
    },
  };

  return (
    <>
      <div className="px-3 appear-anim" style={{ width: '100%', paddingBottom: '36px' }}>
        <Widgets />
      </div>
      <div className="px-3 appear-anim" style={{ width: '100%', paddingBottom: '30px' }}>
        {sectionsPageRightMenu.length > 0 && (
          <>
            <Box mb={3}>
              <span style={{ color: palette.text.title }}>
                <Text tId={'CONTENTS'} />
              </span>
            </Box>
            <div style={indicatorLineStyle} />
            {sectionsPageRightMenu.map((section, key) => {
              return (
                <Fragment key={key}>
                  {section.textId && (
                    <div
                      key={`section-${key}`}
                      style={{
                        height: '25px',
                        marginBottom: '10px',
                      }}
                    >
                      <HashLink key={`hash-link-${key}`} to={`#${section.textId.toLowerCase()}`} smooth>
                        <Button
                          variant={'text'}
                          style={{
                            padding: '4px 15px 0',
                            marginLeft: '-15px',
                            borderRadius: '0px',
                            height: '29px',
                            marginTop: '-2px',
                          }}
                        >
                          <Font
                            key={`span-${key}`}
                            variant={'subtitle2'}
                            style={
                              !loading && Number(sectionIndex) === key ? linkTextStyle.active : linkTextStyle.default
                            }
                          >
                            <Text tId={section.textId} />
                          </Font>
                        </Button>
                      </HashLink>
                    </div>
                  )}
                </Fragment>
              );
            })}
          </>
        )}
      </div>
    </>
  );
}
